import PropTypes from 'prop-types';
import { useRef, useEffect } from 'react';
import { FeatureGroup } from 'react-leaflet';
import { toGeoJson } from '../utils';
import './map.css';

// Get Leaflet from global
const L = window.L;

/**
 * AnnotationsList now imperatively updates its FeatureGroup on prop changes.
 * This ensures updates even if the parent UavMap does not re-render.
 */
const AnnotationsList = ({ annotations, selectedAnnotation, onAnnotationSelected }) => {
  const fgRef = useRef(null);

  useEffect(() => {
    if (!fgRef.current) return;
    const fg = fgRef.current.leafletElement;
    // Clear existing layers
    fg.clearLayers();

    // Add each annotation as a GeoJSON layer
    annotations.forEach(annotation => {
      const geoJson = toGeoJson(annotation);
      L.geoJSON(geoJson, {
        style: feature => {
          const isSelected = selectedAnnotation === annotation._id;
          const extraWeight = isSelected ? 2 : 0;
          return {
            color: feature.properties?.stroke || '#3388ff',
            weight: (feature.properties?.['stroke-width'] || 2) + extraWeight,
            opacity: feature.properties?.['stroke-opacity'] ?? 1.0,
            fillColor: feature.properties?.['fill-color'] || '#3388ff',
            fillOpacity: feature.properties?.['fill-opacity'] ?? 0.2,
          };
        },
        pointToLayer: (feature, latlng) => {
        //  const isSelected = selectedAnnotation === annotation._id;
        //  const extraWeight = isSelected ? 2 : 0;
          return L.marker(latlng);
        },
        onEachFeature: (feature, layer) => {
          // Bind popup if provided
          const popupText = feature.properties?.popupContent || feature.properties?.featureTitle;
          if (popupText) {
            layer.bindPopup(popupText);
            layer.on('mouseover', () => layer.openPopup());
            layer.on('mouseout', () => layer.closePopup());
          }
          // Click to select
          layer.on('click', () => onAnnotationSelected(annotation._id));
        }
      }).addTo(fg);
    });
  }, [annotations, selectedAnnotation, onAnnotationSelected]);

  // Render an empty FeatureGroup; it will be populated via imperative updates
  return <FeatureGroup ref={fgRef} />;
};

AnnotationsList.propTypes = {
  annotations: PropTypes.array.isRequired,
  selectedAnnotation: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onAnnotationSelected: PropTypes.func,
};

AnnotationsList.defaultProps = {
  selectedAnnotation: null,
  onAnnotationSelected: () => {},
};

export default AnnotationsList;
